<script setup lang="ts">
import { computed } from 'vue'
import { formatMoney } from '@/utils/formatMoney'

/**
 * Δ column cell for the client card visits table. Renders a signed money
 * amount (in minor currency units) using the app's `formatMoney` so display
 * stays consistent with the rest of the app, then colors / weights the
 * value by `kind`:
 *
 * - `lesson`  – default text color, mono digits, no leading sign for
 *   negative numbers because the `-` is already part of the number.
 * - `payment` – positive-money color, semibold; explicit leading `+` to
 *   reinforce the credit direction.
 *
 * When `amountMinor` is undefined the cell falls back to the muted
 * placeholder so empty rows don't render a stray `0.00`.
 *
 * When `basePriceMinor` is set (a lesson billed at a discount —
 * `MergedRow.basePriceMinor` is only populated when it differs from the
 * charged amount), a small struck-through "was" line renders above the
 * charged amount so the discount is visible without affecting the balance
 * math, which always runs off the charged price.
 */
const props = withDefaults(
  defineProps<{
    /** Signed money delta in minor currency units (e.g. cents). Negative for lessons, positive for payments. */
    amountMinor: number | undefined
    /** Discriminator from `MergedRow.kind` driving color + weight. */
    kind: 'lesson' | 'payment'
    /** Pre-discount price in minor units (lessons only); shown struck-through above `amountMinor` when set. */
    basePriceMinor?: number
    /** Lesson covered by a subscription: the amount renders muted and regular weight. */
    covered?: boolean
    /** Placeholder shown when `amountMinor` is missing. Defaults to `–`. */
    emptyPlaceholder?: string
    /** Forwarded from `DynamicTable` column config; caps cell width when set. */
    maxWidth?: string
  }>(),
  {
    basePriceMinor: undefined,
    covered: false,
    emptyPlaceholder: '–',
    maxWidth: undefined,
  },
)

const hasValue = computed(
  () => typeof props.amountMinor === 'number' && Number.isFinite(props.amountMinor),
)

const formatted = computed(() => {
  if (!hasValue.value) return null
  /*
   * `formatMoney` already prefixes a `-` for negatives. We only add an
   * explicit `+` for positive payment values so the credit direction is
   * unambiguous; lessons are rendered without a leading sign so the row
   * reads as the cost itself (e.g. `-23.00`).
   */
  const out = formatMoney({ amount: props.amountMinor! }).formatted
  if (props.amountMinor! > 0 && props.kind === 'payment') return `+${out}`
  return out
})

const basePriceFormatted = computed(() => {
  if (typeof props.basePriceMinor !== 'number' || !Number.isFinite(props.basePriceMinor)) {
    return null
  }
  return formatMoney({ amount: props.basePriceMinor }).formatted
})
</script>

<template>
  <td
    class="signed-amount-cell font-tufte-mono"
    :class="[`signed-amount-cell--${props.kind}`, { 'signed-amount-cell--covered': props.covered }]"
    :style="props.maxWidth ? { maxWidth: props.maxWidth, overflowWrap: 'break-word' } : undefined"
  >
    <span v-if="basePriceFormatted" class="signed-amount-cell__base">{{ basePriceFormatted }}</span>
    <span v-if="hasValue">{{ formatted }}</span>
    <span v-else class="text-tufte-muted">{{ emptyPlaceholder }}</span>
  </td>
</template>

<style scoped>
.signed-amount-cell {
  font-family: var(--font-tufte-mono);
  text-align: right;
  /* Inherit `font-size` from the surrounding table so the Δ values render
   * at the same density as the rest of the row (the client card sets the
   * table to `--text-tufte-xs`). The previous explicit `--text-tufte-sm`
   * read as visually heavier than the date / type cells next to it; the
   * user's request was to "decrease little bit font of numbers in last
   * two columns", and inheriting the body size achieves exactly that. */
}

.signed-amount-cell__base {
  /* Struck-through pre-discount price, stacked above the charged amount
   * (mirrors `DateTimeCell`'s two-line date/time stack in the column next
   * to it). Smaller and muted so the charged amount stays the visual
   * focus of the cell. */
  display: block;
  text-decoration: line-through;
  color: var(--color-tufte-muted);
  font-size: 0.85em;
  opacity: 0.75;
}

.signed-amount-cell--lesson {
  /* Lessons read as costs in the default text color (mock: `td-delta.lesson`). */
  color: var(--color-tufte-text);
}

.signed-amount-cell--covered {
  /* Covered by a subscription: nothing was charged, so it reads as a quiet 0. */
  color: var(--color-tufte-muted);
  font-weight: 400;
}

.signed-amount-cell--payment {
  /* Payments mirror the green positive-money color and bold weight from the mock. */
  color: var(--color-money-positive);
  font-weight: 600;
}
</style>
